import { defineStore } from 'pinia';
import { ref } from 'vue';
import {
	ANALYTICS_EVENT_DESCRIPTIONS,
	AUTOAPPLY_QUEUE_EVENTS,
	HOME_EVENTS,
	JOURNEY_EVENTS,
} from '../constants/analytics-events';
import { track } from '../api/track-event';

/**
 * User event types for tracking important user milestones
 *
 * `interview_practice_started` and `gmail_connected` are also Your Journey names (PRD §14),
 * so they arrive from the `JOURNEY_EVENTS` spread instead of being declared here — one
 * string literal per event name. The keys and values are identical to what they replaced.
 */
export const USER_EVENTS = {
	ACCOUNT_CREATED: 'account_created',
	ONBOARDING_COMPLETED: 'onboarding_completed',
	RESUME_CREATED: 'resume_created',
	COVER_LETTER_CREATED: 'cover_letter_created',
	NETWORK_MESSAGE_GENERATED: 'network_message_generated',
	JOB_APPLIED: 'job_applied',
	SCORE_VIEWED: 'm3_score_viewed',
	EXIT_INTENT_SHOWN: 'exit_intent_shown',
	EXIT_INTENT_LEAVE: 'exit_intent_leave',
	EXIT_INTENT_STAY: 'exit_intent_stay',
	CANCELLATION_REASON_SELECTED: 'cancellation_reason_selected',
	SUBSCRIPTION_CANCELLED: 'subscription_cancelled',
	APPLY_CONFIRM_SHOWN: 'apply_confirm_shown',
	APPLY_CONFIRM_YES: 'apply_confirm_yes',
	APPLY_CONFIRM_NO: 'apply_confirm_no',
	AUTOAPPLY_TECHNICAL_LIMIT_SHOWN: 'autoapply_technical_limit_shown',
	...HOME_EVENTS,
	...JOURNEY_EVENTS,
	...AUTOAPPLY_QUEUE_EVENTS,
};

/**
 * Event descriptions for each user event
 */
const EVENT_DESCRIPTIONS = {
	[USER_EVENTS.ACCOUNT_CREATED]: 'User successfully registers an account',
	[USER_EVENTS.ONBOARDING_COMPLETED]: 'User completed onboarding process',
	[USER_EVENTS.RESUME_CREATED]: 'User created resume with ResumAI',
	[USER_EVENTS.COVER_LETTER_CREATED]: 'User created cover letter with CoverLetterAI',
	[USER_EVENTS.NETWORK_MESSAGE_GENERATED]: 'User generated a message using NetworkAI',
	[USER_EVENTS.JOB_APPLIED]: 'User applied to at least one job via JobBoardAI',
	[USER_EVENTS.SCORE_VIEWED]: 'User viewed their initial resume score in onboarding-v2',
	[USER_EVENTS.EXIT_INTENT_SHOWN]: 'AutoApply exit-intent modal shown before the first application',
	[USER_EVENTS.EXIT_INTENT_LEAVE]: 'User clicked \'Leave Anyway\' on the AutoApply exit-intent modal',
	[USER_EVENTS.EXIT_INTENT_STAY]: 'User clicked \'Finish My First Application\' on the AutoApply exit-intent modal',
	[USER_EVENTS.CANCELLATION_REASON_SELECTED]: 'User picked a reason on the subscription cancellation flow reason step',
	[USER_EVENTS.SUBSCRIPTION_CANCELLED]: 'User completed a subscription cancellation',
	[USER_EVENTS.APPLY_CONFIRM_SHOWN]: '"Did you apply?" prompt shown after an external application',
	[USER_EVENTS.APPLY_CONFIRM_YES]: 'User confirmed an external application and added it to JobTrackerAI',
	[USER_EVENTS.APPLY_CONFIRM_NO]: 'User answered "Not yet" to the "Did you apply?" prompt',
	[USER_EVENTS.AUTOAPPLY_TECHNICAL_LIMIT_SHOWN]: 'AutoApply technical limit modal shown to a paid user',
	...ANALYTICS_EVENT_DESCRIPTIONS,
};

export const useUserEvents = defineStore('userEvents', () => {
	// Store tracked events history
	const eventHistory = ref([]);
  
	/**
   * Track a user event
   * 
   * @param {string} eventName - One of the USER_EVENTS constants
   * @param {Object} metadata - Optional event properties, persisted onto the user_actions row
   * @returns {Promise} - The API request promise
   */
	const trackEvent = async (eventName, metadata = {}) => {
    
		if (!Object.values(USER_EVENTS).includes(eventName)) {
			return;
		}
    
		const eventData = {
			action_type: eventName,
			action_description: EVENT_DESCRIPTIONS[eventName],
			metadata
		};
    
		// Send to API
		try {
			const response = await track(eventData);
			return response;
		} catch (error) {
			console.error('Error tracking event:', error);
			throw error;
		}
	};
  
	/**
   * Check if a specific event has been tracked
   * 
   * @param {string} eventName - One of the USER_EVENTS constants
   * @returns {boolean} - Whether the event has been tracked
   */
	const hasTrackedEvent = (eventName) => {
		return eventHistory.value.some((event) => event.action_type === eventName);
	};
  
	/**
   * Clear event history (for testing or user logout)
   */
	const clearEventHistory = () => {
		eventHistory.value = [];
	};
  
	return {
		eventHistory,
		trackEvent,
		hasTrackedEvent,
		clearEventHistory,
		USER_EVENTS
	};
});
